import React, { useState, useMemo, useCallback } from 'react';
import Plot from 'react-plotly.js';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ChevronDown, ChevronUp, Award, Layers, Target, BarChart3, GitBranch } from 'lucide-react';
import GraphSidebar, { COLOR_SCHEMES } from '@/components/GraphSidebar';

const PRAKRITI_BG = '#F9F8F6';
const PRAKRITI_CARD = '#FFFFFF';
const PRAKRITI_GREEN = '#6B8E7B';
const PRAKRITI_EARTH = '#C87A5E';
const PRAKRITI_TEXT = '#2C2A28';
const PRAKRITI_MUTED = '#8A8580';

function D2AnalysisView({ data }) {
  const [expandedSections, setExpandedSections] = useState({
    anova: true, tocher: true, d2matrix: true, traitContrib: true,
    clusterDist: true, clusterMeans: true, recommendations: true,
    dendrogram: true, scatter3d: true
  });
  const [dendrogramCutoff, setDendrogramCutoff] = useState(null);
  const [scatterColorBy, setScatterColorBy] = useState('cluster');
  const [colorScheme, setColorScheme] = useState('default');
  const [labelFontSize, setLabelFontSize] = useState(11);
  const [viewMode, setViewMode] = useState('3d');

  const CLUSTER_COLORS = COLOR_SCHEMES[colorScheme]?.colors || COLOR_SCHEMES.default.colors;

  const toggle = useCallback((key) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  // Compute default dendrogram cutoff
  const defaultCutoff = useMemo(() => {
    if (!data.dendrogram_linkage?.length) return 5;
    const heights = data.dendrogram_linkage.map(l => l[2]);
    return parseFloat((heights.reduce((a, b) => a + b, 0) / heights.length).toFixed(2));
  }, [data.dendrogram_linkage]);

  const activeCutoff = dendrogramCutoff ?? defaultCutoff;

  // ---- Collapsible Section Wrapper ----
  const Section = ({ id, icon: Icon, title, badge, children }) => (
    <Card className="border border-[#E8E6E1] shadow-sm overflow-hidden" data-testid={`d2-section-${id}`}>
      <CardHeader
        className="cursor-pointer hover:bg-[#F9F8F6] transition-colors"
        onClick={() => toggle(id)}
      >
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-3 text-base sm:text-lg">
            <Icon size={20} className="text-[#6B8E7B] flex-shrink-0" />
            {title}
            {badge && (
              <span className="ml-2 text-xs bg-[#EAF0EC] text-[#2C2A28] px-2 py-0.5 rounded-full font-medium">{badge}</span>
            )}
          </CardTitle>
          {expandedSections[id] ? <ChevronUp size={18} className="text-[#8A8580]" /> : <ChevronDown size={18} className="text-[#8A8580]" />}
        </div>
      </CardHeader>
      {expandedSections[id] && <CardContent>{children}</CardContent>}
    </Card>
  );

  // ---- Pooled ANOVA Table ----
  const renderANOVA = () => {
    const anova = data.pooled_anova;
    if (!anova) return null;
    const traits = Object.keys(anova);
    return (
      <Section id="anova" icon={BarChart3} title="Pooled ANOVA" badge={`${traits.length} traits`}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse" data-testid="d2-anova-table">
            <thead>
              <tr className="bg-[#EAF0EC]">
                {['Trait', 'DF (Treat)', 'DF (Error)', 'SS (Treat)', 'MS (Treat)', 'MS (Error)', 'F-value', 'P-value', 'Sig.'].map(h => (
                  <th key={h} className="border border-[#D5D1CC] px-3 py-2.5 text-left font-semibold text-xs uppercase tracking-wider text-[#2C2A28]">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {traits.map(trait => {
                const r = anova[trait];
                return (
                  <tr key={trait} className="hover:bg-[#F5F4F2] transition-colors">
                    <td className="border border-[#E8E6E1] px-3 py-2 font-medium text-[#2C2A28]">{trait}</td>
                    <td className="border border-[#E8E6E1] px-3 py-2 text-right font-mono text-[#5E5A56]">{r.DF_Treatment}</td>
                    <td className="border border-[#E8E6E1] px-3 py-2 text-right font-mono text-[#5E5A56]">{r.DF_Error}</td>
                    <td className="border border-[#E8E6E1] px-3 py-2 text-right font-mono text-[#5E5A56]">{r.SS_Treatment?.toFixed(2)}</td>
                    <td className="border border-[#E8E6E1] px-3 py-2 text-right font-mono text-[#5E5A56]">{r.MS_Treatment?.toFixed(4)}</td>
                    <td className="border border-[#E8E6E1] px-3 py-2 text-right font-mono text-[#5E5A56]">{r.MS_Error?.toFixed(4)}</td>
                    <td className="border border-[#E8E6E1] px-3 py-2 text-right font-mono text-[#5E5A56]">{r.F?.toFixed(4)}</td>
                    <td className="border border-[#E8E6E1] px-3 py-2 text-right font-mono text-[#5E5A56]">{r.P?.toFixed(6)}</td>
                    <td className="border border-[#E8E6E1] px-3 py-2 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${
                        r.Sig === '**' ? 'bg-emerald-500 text-white' :
                        r.Sig === '*' ? 'bg-amber-500 text-white' :
                        'bg-gray-200 text-gray-600'
                      }`}>{r.Sig}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="text-xs text-[#8A8580] mt-2">** p&lt;0.01 (Highly Significant) | * p&lt;0.05 (Significant) | ns = Not Significant</p>
        </div>
      </Section>
    );
  };

  // ---- Tocher's Clustering ----
  const renderTocher = () => {
    const clusters = data.tocher_clusters;
    if (!clusters) return null;
    return (
      <Section id="tocher" icon={Layers} title="Tocher's Clustering" badge={`${Object.keys(clusters).length} clusters`}>
        <div className="space-y-3" data-testid="d2-tocher-table">
          {Object.entries(clusters).map(([name, info]) => (
            <div key={name} className="bg-[#F9F8F6] rounded-2xl p-4 border border-[#E8E6E1]">
              <div className="flex items-center gap-3 mb-2">
                <div
                  className="w-4 h-4 rounded-full flex-shrink-0"
                  style={{ backgroundColor: CLUSTER_COLORS[parseInt(name.split(' ')[1]) - 1] || PRAKRITI_GREEN }}
                />
                <span className="font-semibold text-[#2C2A28] text-sm">{name}</span>
                <span className="text-xs bg-[#EAF0EC] px-2 py-0.5 rounded-full text-[#5E5A56]">{info.count} genotype{info.count > 1 ? 's' : ''}</span>
              </div>
              <div className="flex flex-wrap gap-1.5 ml-7">
                {info.members.map(m => (
                  <span key={m} className="inline-block bg-white border border-[#D5D1CC] px-2.5 py-1 rounded-lg text-xs font-mono text-[#2C2A28]">{m}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Section>
    );
  };

  // ---- D² Distance Matrix ----
  // Backend returns the matrix at `distance_matrices.mahalanobis`
  // (cluster_engine.py); legacy callers may still send `d2_distance_matrix`.
  // Honour both shapes so existing tests + new comprehensive runs both
  // render the matrix.
  const renderD2Matrix = () => {
    const matrix = data.d2_distance_matrix || data.distance_matrices?.mahalanobis;
    if (!matrix) return null;
    const genos = Object.keys(matrix);
    return (
      <Section id="d2matrix" icon={Target} title="Pairwise D² Distance Matrix" badge={`${genos.length}x${genos.length}`}>
        <div className="overflow-x-auto" data-testid="d2-distance-matrix">
          <table className="text-xs border-collapse">
            <thead>
              <tr className="bg-[#EAF0EC]">
                <th className="border border-[#D5D1CC] px-2 py-1.5 sticky left-0 bg-[#EAF0EC] z-10"></th>
                {genos.map(g => (
                  <th key={g} className="border border-[#D5D1CC] px-2 py-1.5 text-center font-semibold min-w-[60px]">{g}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {genos.map(gi => (
                <tr key={gi}>
                  <td className="border border-[#E8E6E1] px-2 py-1.5 font-semibold bg-[#F5F4F2] sticky left-0 z-10">{gi}</td>
                  {genos.map(gj => {
                    const val = matrix[gi]?.[gj] || 0;
                    const maxVal = Math.max(...genos.flatMap(a => genos.map(b => matrix[a]?.[b] || 0)));
                    const intensity = maxVal > 0 ? val / maxVal : 0;
                    const bg = gi === gj ? '#F5F4F2' :
                      intensity > 0.7 ? '#FADBD8' :
                      intensity > 0.4 ? '#FEF5E7' :
                      intensity > 0.1 ? '#EAFAF1' : '#FFFFFF';
                    return (
                      <td key={gj} className="border border-[#E8E6E1] px-2 py-1.5 text-center font-mono"
                        style={{ backgroundColor: bg }}>
                        {gi === gj ? '-' : val.toFixed(2)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
    );
  };

  // ---- Trait Contribution ----
  const renderTraitContribution = () => {
    const tc = data.trait_contributions;
    if (!tc) return null;
    const sorted = Object.entries(tc).sort((a, b) => a[1].rank - b[1].rank);
    return (
      <Section id="traitContrib" icon={BarChart3} title="Trait Contribution to D²" badge="Times ranked 1st">
        <p className="text-xs text-[#8A8580] mb-3" data-testid="d2-trait-contribution-method">
          Singh &amp; Chaudhary (1977) method: for every genotype pair the trait contributing most to that pair's D²
          is counted; % contribution = (times ranked 1st ÷ {(() => { const n = Object.values(tc).reduce((s, i) => s + (i.times_ranked_first || 0), 0); return n; })()} pairs) × 100.
        </p>
        <div className="space-y-2" data-testid="d2-trait-contribution">
          {sorted.map(([trait, info]) => (
            <div key={trait} className="flex items-center gap-3">
              <span className="w-6 text-center text-xs font-bold text-[#8A8580]">#{info.rank}</span>
              <span className="w-40 text-sm font-medium text-[#2C2A28] truncate">{trait}</span>
              <div className="flex-1 bg-[#EAF0EC] rounded-full h-6 relative overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${Math.max(info.percentage, 2)}%`,
                    backgroundColor: info.percentage > 20 ? '#2ECC71' : info.percentage > 10 ? PRAKRITI_GREEN : '#BDC3C7'
                  }}
                />
                <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-[#2C2A28]">
                  {info.percentage.toFixed(2)}%
                </span>
              </div>
              {info.times_ranked_first != null && (
                <span className="w-20 text-right text-xs text-[#8A8580]">{info.times_ranked_first}× 1st</span>
              )}
            </div>
          ))}
        </div>
      </Section>
    );
  };

  // ---- Inter/Intra Cluster Distance ----
  const renderClusterDist = () => {
    const cd = data.cluster_distance_matrix;
    if (!cd) return null;
    const clusters = Object.keys(cd);
    return (
      <Section id="clusterDist" icon={GitBranch} title="Intra- & Inter-Cluster Distance" badge={`${clusters.length} clusters`}>
        <div className="overflow-x-auto" data-testid="d2-cluster-distance">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-[#EAF0EC]">
                <th className="border border-[#D5D1CC] px-4 py-2.5 text-left font-semibold"></th>
                {clusters.map(c => (
                  <th key={c} className="border border-[#D5D1CC] px-4 py-2.5 text-center font-semibold text-[#2C2A28]">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {clusters.map(ci => (
                <tr key={ci} className="hover:bg-[#F5F4F2]">
                  <td className="border border-[#E8E6E1] px-4 py-2 font-semibold bg-[#F5F4F2] text-[#2C2A28]">{ci}</td>
                  {clusters.map(cj => {
                    const val = cd[ci]?.[cj] || 0;
                    const isDiag = ci === cj;
                    return (
                      <td key={cj} className={`border border-[#E8E6E1] px-4 py-2 text-center font-mono ${
                        isDiag ? 'bg-[#EAF0EC] text-[#6B8E7B] font-bold' : 'text-[#2C2A28]'
                      }`}>
                        {val.toFixed(2)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-[#8A8580] mt-2">Diagonal = Intra-cluster distance | Off-diagonal = Inter-cluster distance (D values)</p>
        </div>
      </Section>
    );
  };

  // ---- Cluster Means ----
  const renderClusterMeans = () => {
    const cm = data.cluster_means;
    if (!cm) return null;
    const clusters = Object.keys(cm);
    const traits = cm[clusters[0]] ? Object.keys(cm[clusters[0]]) : [];
    return (
      <Section id="clusterMeans" icon={BarChart3} title="Cluster Means" badge={`${traits.length} traits`}>
        <div className="overflow-x-auto" data-testid="d2-cluster-means">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-[#EAF0EC]">
                <th className="border border-[#D5D1CC] px-4 py-2.5 text-left font-semibold text-[#2C2A28]">Cluster</th>
                {traits.map(t => (
                  <th key={t} className="border border-[#D5D1CC] px-3 py-2.5 text-center font-semibold text-xs text-[#2C2A28]">{t}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {clusters.map(c => (
                <tr key={c} className="hover:bg-[#F5F4F2]">
                  <td className="border border-[#E8E6E1] px-4 py-2 font-semibold text-[#2C2A28]">{c}</td>
                  {traits.map(t => {
                    const val = cm[c]?.[t];
                    const allVals = clusters.map(cl => cm[cl]?.[t] || 0);
                    const maxV = Math.max(...allVals);
                    const isMax = val === maxV;
                    return (
                      <td key={t} className={`border border-[#E8E6E1] px-3 py-2 text-center font-mono text-xs ${
                        isMax ? 'bg-emerald-50 font-bold text-emerald-700' : 'text-[#5E5A56]'
                      }`}>
                        {typeof val === 'number' ? val.toFixed(2) : '-'}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-[#8A8580] mt-2">Highlighted = highest cluster mean for trait</p>
        </div>
      </Section>
    );
  };

  // ---- Parental Recommendations ----
  const renderRecommendations = () => {
    const recs = data.parental_recommendations;
    if (!recs?.length) return null;
    return (
      <Section id="recommendations" icon={Award} title="Parental Recommendations for Hybridization" badge="Auto-generated">
        <div className="space-y-3" data-testid="d2-recommendations">
          {recs.map((rec, i) => (
            <div key={i} className="bg-gradient-to-r from-[#EAF0EC] to-[#F9F8F6] rounded-2xl p-5 border border-[#D5D1CC]">
              <p className="text-sm font-semibold text-[#2C2A28] mb-3">
                For <span className="text-[#6B8E7B]">{rec.trait}</span>:
              </p>
              <div className="flex items-center gap-4 flex-wrap">
                <div className="bg-white rounded-xl px-4 py-2.5 border border-[#E8E6E1] text-center">
                  <p className="text-xs text-[#8A8580] mb-1">{rec.parent_1_cluster}</p>
                  <p className="font-bold text-lg text-[#2C2A28]">{rec.parent_1}</p>
                  <p className="text-xs font-mono text-[#6B8E7B]">{rec.parent_1_value}</p>
                </div>
                <div className="text-2xl text-[#C87A5E] font-bold">&times;</div>
                <div className="bg-white rounded-xl px-4 py-2.5 border border-[#E8E6E1] text-center">
                  <p className="text-xs text-[#8A8580] mb-1">{rec.parent_2_cluster}</p>
                  <p className="font-bold text-lg text-[#2C2A28]">{rec.parent_2}</p>
                  <p className="text-xs font-mono text-[#6B8E7B]">{rec.parent_2_value}</p>
                </div>
                <div className="text-xs text-[#8A8580] ml-2">
                  Inter-cluster D = <span className="font-mono font-bold text-[#C87A5E]">{rec.inter_cluster_distance}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Section>
    );
  };

  // ---- Interactive Dendrogram (Plotly) ----
  const renderDendrogram = () => {
    const link = data.dendrogram_linkage;
    const labels = data.genotype_labels;
    if (!link?.length || !labels?.length) return null;

    const n = labels.length;
    // Build dendrogram from linkage manually for Plotly
    const xCoords = {};
    const plotData = [];
    let nextX = 0;

    // Assign x positions to leaves
    // Use a simple ordering
    for (let i = 0; i < n; i++) {
      xCoords[i] = i;
    }

    // Build merge trace lines
    for (let step = 0; step < link.length; step++) {
      const [c1, c2, dist] = link[step];
      const newCluster = n + step;
      const x1 = xCoords[c1] ?? 0;
      const x2 = xCoords[c2] ?? 0;
      xCoords[newCluster] = (x1 + x2) / 2;

      // Horizontal and vertical lines forming U-shape
      const y1 = c1 < n ? 0 : link[c1 - n]?.[2] || 0;
      const y2 = c2 < n ? 0 : link[c2 - n]?.[2] || 0;

      plotData.push({
        x: [x1, x1, x2, x2],
        y: [y1, dist, dist, y2],
        mode: 'lines',
        line: { color: dist > activeCutoff ? CLUSTER_COLORS[1] || '#E74C3C' : CLUSTER_COLORS[0] || PRAKRITI_GREEN, width: 2 },
        hoverinfo: 'text',
        text: `D² = ${dist.toFixed(2)}`,
        showlegend: false
      });
    }

    return (
      <Section id="dendrogram" icon={GitBranch} title="Dynamic Dendrogram" badge="Interactive">
        <div className="mb-4 flex items-center gap-4 flex-wrap">
          <label className="text-sm text-[#5E5A56] font-medium">Cut-off threshold:</label>
          <input
            type="range"
            min={0}
            max={Math.max(...link.map(l => l[2])) * 1.1}
            step={0.1}
            value={activeCutoff}
            onChange={(e) => setDendrogramCutoff(parseFloat(e.target.value))}
            className="w-48 accent-[#6B8E7B]"
            data-testid="dendrogram-cutoff-slider"
          />
          <span className="font-mono text-sm text-[#2C2A28] bg-[#EAF0EC] px-3 py-1 rounded-full">{activeCutoff.toFixed(2)}</span>
        </div>
        <Plot
          data={[
            ...plotData,
            {
              x: Array.from({ length: n }, (_, i) => i),
              y: Array(n).fill(0),
              mode: 'markers+text',
              type: 'scatter',
              marker: { size: 10, color: CLUSTER_COLORS[0] || PRAKRITI_GREEN, symbol: 'diamond' },
              text: labels,
              textposition: 'bottom center',
              textfont: { size: labelFontSize, family: 'monospace' },
              showlegend: false,
              hoverinfo: 'text'
            },
            {
              x: [-0.5, n - 0.5],
              y: [activeCutoff, activeCutoff],
              mode: 'lines',
              line: { color: '#E74C3C', width: 2, dash: 'dash' },
              name: `Cutoff (${activeCutoff.toFixed(1)})`,
              hoverinfo: 'name'
            }
          ]}
          layout={{
            title: { text: "Ward's Dendrogram — Drag slider to adjust cut-off", font: { size: 14, color: PRAKRITI_TEXT } },
            xaxis: { title: 'Genotype', showticklabels: false, zeroline: false },
            yaxis: { title: 'D² Distance', zeroline: false },
            paper_bgcolor: PRAKRITI_BG,
            plot_bgcolor: '#FFFFFF',
            margin: { t: 50, b: 50, l: 60, r: 20 },
            height: 420,
            showlegend: true,
            legend: { x: 0.8, y: 1 }
          }}
          config={{ responsive: true, displayModeBar: true, modeBarButtonsToRemove: ['lasso2d', 'select2d'] }}
          style={{ width: '100%' }}
        />
      </Section>
    );
  };

  // ---- Interactive 2D/3D Scatter (Plotly) ----
  const renderScatter = () => {
    const points = data.pca_3d_data;
    const varExp = data.pca_variance_explained || [];
    if (!points?.length) return null;

    const clusters = [...new Set(points.map(p => p.cluster))].sort();

    const traces = clusters.map(cl => {
      const pts = points.filter(p => p.cluster === cl);
      const baseTrace = {
        x: pts.map(p => p.pc1),
        y: pts.map(p => p.pc2),
        mode: 'markers+text',
        name: `Cluster ${cl}`,
        text: pts.map(p => p.genotype),
        textposition: 'top center',
        textfont: { size: labelFontSize, family: 'monospace' },
        marker: {
          size: 8,
          color: CLUSTER_COLORS[(cl - 1) % CLUSTER_COLORS.length] || PRAKRITI_GREEN,
          opacity: 0.85,
          line: { color: '#2C2A28', width: 1 }
        },
      };
      if (viewMode === '3d') {
        return {
          ...baseTrace,
          z: pts.map(p => p.pc3),
          type: 'scatter3d',
          hovertemplate: '%{text}<br>PC1: %{x:.3f}<br>PC2: %{y:.3f}<br>PC3: %{z:.3f}<extra>Cluster ' + cl + '</extra>'
        };
      }
      return {
        ...baseTrace,
        type: 'scatter',
        hovertemplate: '%{text}<br>PC1: %{x:.3f}<br>PC2: %{y:.3f}<extra>Cluster ' + cl + '</extra>'
      };
    });

    const layout = viewMode === '3d' ? {
      scene: {
        xaxis: { title: `PC1 (${varExp[0] || 0}%)`, backgroundcolor: '#F9F8F6', gridcolor: '#E8E6E1' },
        yaxis: { title: `PC2 (${varExp[1] || 0}%)`, backgroundcolor: '#F9F8F6', gridcolor: '#E8E6E1' },
        zaxis: { title: `PC3 (${varExp[2] || 0}%)`, backgroundcolor: '#F9F8F6', gridcolor: '#E8E6E1' },
        bgcolor: '#FFFFFF'
      },
      paper_bgcolor: PRAKRITI_BG, margin: { t: 30, b: 20, l: 20, r: 20 }, height: 520,
      showlegend: true, legend: { x: 0.02, y: 0.98 }
    } : {
      xaxis: { title: `PC1 (${varExp[0] || 0}%)`, zeroline: false, gridcolor: '#E8E6E1' },
      yaxis: { title: `PC2 (${varExp[1] || 0}%)`, zeroline: false, gridcolor: '#E8E6E1' },
      paper_bgcolor: PRAKRITI_BG, plot_bgcolor: '#FFFFFF',
      margin: { t: 30, b: 50, l: 60, r: 20 }, height: 480,
      showlegend: true, legend: { x: 0.02, y: 0.98 }
    };

    return (
      <Section id="scatter3d" icon={Target} title={`${viewMode === '3d' ? '3D' : '2D'} PCA Scatter Plot`} badge="Zoom & Rotate">
        <div className="flex gap-4">
          <div className="flex-1">
            <Plot
              data={traces}
              layout={layout}
              config={{ responsive: true, displayModeBar: true, scrollZoom: true }}
              style={{ width: '100%' }}
            />
          </div>
          <div className="w-52 shrink-0">
            <GraphSidebar
              colorScheme={colorScheme}
              onColorSchemeChange={setColorScheme}
              fontSize={labelFontSize}
              onFontSizeChange={setLabelFontSize}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              show2D3DToggle={true}
              onReset={() => { setColorScheme('default'); setLabelFontSize(11); setViewMode('3d'); }}
            />
          </div>
        </div>
      </Section>
    );
  };

  return (
    <div className="space-y-5" data-testid="d2-analysis-view">
      {/* Summary Header */}
      <div className="bg-gradient-to-br from-[#EAF0EC] via-[#F5F4F2] to-[#FDF6F0] rounded-[20px] p-6 border border-[#D5D1CC]" data-testid="d2-summary">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="text-center">
            <p className="text-xs text-[#8A8580] uppercase tracking-wider mb-1">Genotypes</p>
            <p className="text-3xl font-bold text-[#2C2A28]">{data.n_genotypes}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-[#8A8580] uppercase tracking-wider mb-1">Traits</p>
            <p className="text-3xl font-bold text-[#6B8E7B]">{data.n_traits}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-[#8A8580] uppercase tracking-wider mb-1">Clusters</p>
            <p className="text-3xl font-bold text-[#C87A5E]">{data.n_clusters}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-[#8A8580] uppercase tracking-wider mb-1">Top Contributor</p>
            <p className="text-sm font-bold text-[#2C2A28] mt-1">
              {data.trait_contributions ? Object.entries(data.trait_contributions).sort((a,b) => a[1].rank - b[1].rank)[0]?.[0] : '-'}
            </p>
          </div>
        </div>
        {data.interpretation && (
          <p className="mt-4 text-sm text-[#5E5A56] leading-relaxed border-t border-[#E8E6E1] pt-4">{data.interpretation}</p>
        )}
      </div>

      {renderANOVA()}
      {renderTocher()}
      {renderTraitContribution()}
      {renderClusterDist()}
      {renderClusterMeans()}
      {renderRecommendations()}
      {renderDendrogram()}
      {renderScatter()}
      {renderD2Matrix()}

      {/* Static Charts (dendrogram + trait bar) */}
      {data.chart && (
        <Card className="border border-[#E8E6E1] shadow-sm">
          <CardHeader>
            <CardTitle className="text-base">Static Charts</CardTitle>
          </CardHeader>
          <CardContent>
            <img
              src={`data:image/png;base64,${data.chart}`}
              alt="D² Analysis Charts"
              className="w-full rounded-xl"
              data-testid="d2-static-chart"
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default D2AnalysisView;
